import { render } from "react-dom";
import { MoveCameraToOverwriteProps, RenderEngine } from "./RenderEngine";
import { Tree } from "./Tree";
import { Box3, Mesh, Vector3, Vector2, Raycaster } from "three"
import { IEnvironmentInfo } from "@viscircle-org/threngine";
import { ClickMesh } from "./ClickMesh";


export class Controller {
  private _renderEngine:RenderEngine;
  private _tree:Tree;
  private _updateUI:()=>void;
  private _raycaster:Raycaster;
  private _onMoveCounter:number = 0;
  private _clickPosition:{x:number, y:number} = null;
  private _roundTour:boolean = false;
  private _activeClickMesh:ClickMesh;

  constructor() {
    this._renderEngine = new RenderEngine();
    this._raycaster = new Raycaster();
  }

  async init(mount:HTMLDivElement, callback:()=>void) {
    this._updateUI = callback;
    await this._renderEngine.init(mount);
    this._tree = new Tree(this._renderEngine.scene);
    this.selectById(this.tree.root.clickMesh.id);

    this._updateUI();
    mount.addEventListener("pointermove", this.onMove);
    mount.addEventListener("mousedown", this.onMouseDown);
    mount.addEventListener("mouseup", this.onMouseUp);
    this._renderEngine.engine.services.render.onBeforeRender.subscribe(() => {
      this.tree.updateHotspots(this._renderEngine.engine.services.render.camera.position);
      return Promise.resolve()
    });
    return Promise.resolve();
  }


  get tree():Tree {
    return this._tree;
  }

  get roundTour():boolean {
    return this._roundTour;
  }

  get activeClickMesh():ClickMesh {
    return this._activeClickMesh;
  }

  public hideAllClickMeshes = () => {
    this._tree.cursor.children.forEach(c => c.clickMesh.hide());
    this._activeClickMesh = null;
    this._updateUI();
  }

  public showClickMeshById = (id:string) => {
    this._activeClickMesh = this._tree.cursor.children.find(c => c.clickMesh.id === id).clickMesh;
    this._activeClickMesh.show();
    this._updateUI();
  }

  public selectById = (id:string, overwriteProps:MoveCameraToOverwriteProps = { orbit: {}, transition: {} }) => {
    const onAnimationFinished = () => {};
    const previousLevel = this.tree.level;
    if (this.tree.level < 0) overwriteProps.transition.time = 0; //No camera transition time on initialization

    console.log("clickables", this.tree.cursor.clickables);

    this.hideAllClickMeshes();
    id = this._tree.selectNodeById(id);
    const mesh = this._tree.selectMesh(id);
    this._tree.handleLights(id);

    this._updateUI();
    let position = new Vector3();
    let rotation = new Vector3(1, 0, 0);
    console.error("goin in", mesh)
    if (mesh?.geometry !== null) {
      mesh.getWorldPosition(position);
      mesh.geometry.computeBoundingBox();
      mesh.geometry.boundingBox.getCenter(position);

      var boundingBox = new Box3();
      boundingBox.copy(mesh.geometry.boundingBox);
      mesh.updateMatrixWorld(true); // ensure world matrix is up to date
      boundingBox.applyMatrix4(mesh.matrixWorld);
      boundingBox.getCenter(position);
      mesh.getWorldDirection(rotation);
    }

    const envInfo:IEnvironmentInfo = this._renderEngine.engine.services.environment.environment.info;
    envInfo.data.tonemappingExposure = 0.35; //should be 0.35 outside and 1 inside
    if (this.tree.level >= 2) envInfo.data.tonemappingExposure = 1;
    this._renderEngine.engine.services.environment.setEnvironmentInfo(envInfo);

    //DEBUG
    // if (previousLevel === 4) return;

    this._renderEngine.moveCameraTo(position, rotation, this._tree.level, this._roundTour, onAnimationFinished, overwriteProps);
  }

  public startRoundTour = async () => {
    this._roundTour = true;
    const startingHotspot = this.tree.cursor.clickables.find((el) => el.name.toLowerCase().includes("hotspot_tour01"));
    const secondHotspot = this.tree.cursor.clickables.find((el) => el.name.toLowerCase().includes("hotspot_court01"));

    const startingHotspotPosition = startingHotspot.getMesh().position;
    const secondHotspotPosition = secondHotspot.getMesh().position;

    const direction = new Vector3();
    const position = new Vector3();

    direction.subVectors(startingHotspotPosition, secondHotspotPosition).normalize().multiplyScalar(0.00001);
    position.addVectors(startingHotspot.getMesh().position, direction);

    await this._renderEngine.engine.services.render.fadeOut(500, "white");
    this.selectById(startingHotspot.id, { orbit: { position: position.toArray(), target: startingHotspotPosition.toArray() }, transition: { time: 0 } });
    this.tree.setActiveHotspotsVisibility(true);
    await this._renderEngine.engine.services.render.fadeIn(1500, "white");
    
    return Promise.resolve();
  }

  public stopRoundTour = async () => {
    this._roundTour = false;
    const firstHotspot = this.tree.cursor.clickables[0]; //We pretend to click a hotspot here just so we can stick with the usual control flow

    await this._renderEngine.engine.services.render.fadeOut(500, "white");
    this.selectById(firstHotspot.id, { transition: { time: 0 } });
    this.tree.setActiveHotspotsVisibility(false);
    await this._renderEngine.engine.services.render.fadeIn(500, "white");

    return Promise.resolve();
  }

  public onMouseDown = (e:MouseEvent) => {
    this._clickPosition = { x: e.clientX, y: e.clientY };
  }

  public onMouseUp = (e:MouseEvent) => {
    //If mouseDown position does not match mouseUp position don't treat it as a click
    if (!this._clickPosition || this._clickPosition.x !== e.clientX || this._clickPosition.y !== e.clientY) return;

    const pointer = new Vector2();
    const rect = this._renderEngine.engine.services.render.renderController.renderer.domElement.getBoundingClientRect();
    pointer.x = ((e.clientX - rect.left) / (rect.right - rect.left)) * 2 - 1;
    pointer.y = - ((e.clientY - rect.top) / (rect.bottom - rect.top)) * 2 + 1;

    this.hideAllClickMeshes();
    if (Math.abs(pointer.x) > 1) return;

    this._raycaster.setFromCamera(pointer, this._renderEngine.engine.services.render.camera);
    const intersects = this._raycaster.intersectObjects(this._tree.getClickMeshes());

    if (intersects.length > 0) {
      const intersectedMesh = (<Mesh>intersects[0].object);
      if (this.roundTour && !intersectedMesh.name.toLowerCase().includes("hotspot")) return; /* Prevent clicks on anything other than hotspots while roundTour mode is active */
      this.selectById(intersectedMesh.uuid)
    }
  }

  public onMove = (event) => {
    //Do not intersect the scene every frame...
    if ((this._onMoveCounter++ % 4) != 0) return;
    if (this._roundTour) return;

    const pointer = new Vector2();
    const rect = this._renderEngine.engine.services.render.renderController.renderer.domElement.getBoundingClientRect();
    pointer.x = ((event.clientX - rect.left) / (rect.right - rect.left)) * 2 - 1;
    pointer.y = - ((event.clientY - rect.top) / (rect.bottom - rect.top)) * 2 + 1;
    this._raycaster.setFromCamera(pointer, this._renderEngine.engine.services.render.camera);

    if (Math.abs(pointer.x) > 1) return;

    const clickMeshes = this._tree.cursor.children.map(c => c.clickMesh.getMesh())
    const intersects = this._raycaster.intersectObjects(clickMeshes);
    this.hideAllClickMeshes();
    if (intersects.length > 0) {
      // this._tree.cursor.children.find(c => c.clickMesh.id === intersects[0].object.uuid).clickMesh.show();
      this.showClickMeshById(intersects[0].object.uuid);
    }
  }
}